import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, getSqlIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { usePipelinesData } from '@pages/Transpiler/usePipelinesData';
import { ArrowNarrowUpRightIcon, EditBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { ColumnsType, Stack, StackItem, Table, TableActions, TableIconButton } from '@prophecy/ui-v3';
import React, { useMemo } from 'react';

import { PipelineInfoType } from '../../../../data/apis/transpileTypes';
import { Error } from '../../Import/utils';
import { getStatusColumn, hasStatusWithError, InfoTableHeader, StyledNameContainer } from '../util';

const getPipelinesColumns = ({
  isSql,
  projectId
}: {
  isSql?: boolean;
  projectId?: string;
}): ColumnsType<PipelineInfoType> => [
  {
    key: 'pipelineName',
    dataIndex: 'pipelineName',
    title: 'Name',
    width: '20%',
    filter: {
      type: 'search',
      searchInputProps: {
        placeholder: 'Search..',
        'data-test-id': 'pipelines-table-name-search-input'
      }
    },
    render: (name) => {
      return (
        <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
          <EntityIcon entity={Entity.Pipeline} size='xs' />
          <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
        </Stack>
      );
    }
  },
  {
    key: 'source',
    width: '20%',
    dataIndex: 'source',
    sortable: true,
    title: 'Source File',
    render: renderColumnWithEllipsis
  },
  {
    key: 'log',
    dataIndex: 'log',
    title: 'Message',
    render: (msg) => renderColumnWithEllipsis(msg, undefined, undefined, true)
  },
  getStatusColumn(),
  {
    key: 'actions',
    dataIndex: 'actions',
    width: '50px',
    render: (_, row) => {
      const entityId = row.id;
      if (hasStatusWithError(row.status)) {
        return null;
      }
      return (isSql && projectId) || entityId ? (
        <TableActions gap={theme.spaces.x4} align='end'>
          <TableIconButton
            icon={<EditBIcon type='default' />}
            elementType='link'
            tooltip={'Edit Pipeline'}
            to={
              isSql
                ? getSqlIDEUrl({ uid: projectId!, entity: Entity.Pipeline, name: row.pipelineName })
                : getIDEUrl({
                    uid: entityId,
                    entity: Entity.Pipeline
                  })
            }
            aria-label={`Edit ${row.pipelineName}`}
            data-test-id='pipelines-table-edit-button'
          />
          {!isSql && (
            <TableIconButton
              tooltip={'Pipeline Detail'}
              icon={<ArrowNarrowUpRightIcon type='default' />}
              elementType='link'
              to={Private_Routes.Pipeline.home.getUrl({ uid: entityId })}
              aria-label={`View ${row.pipelineName} details`}
              data-test-id='pipelines-table-pipeline-detail-button'
            />
          )}
        </TableActions>
      ) : null;
    }
  }
];

export const PipelinesTable = ({
  isBulk,
  id,
  isSql,
  projectId
}: {
  isBulk: boolean;
  id: string;
  isSql?: boolean;
  projectId?: string;
}) => {
  const { data, isError, isLoading } = usePipelinesData(id, isBulk);

  const pipelines = useMemo(() => data?.data?.pipelines || [], [data?.data]);
  const pipelineCols = useMemo(() => getPipelinesColumns({ projectId, isSql }), [isSql, projectId]);

  if (isError) {
    return <Error message='Some error occurred' />;
  }

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12}>
      <InfoTableHeader title='Pipelines' count={pipelines.length} />
      <StackItem grow={'1'}>
        <Table
          fixedHeader={true}
          height='100%'
          width='100%'
          loading={isLoading}
          virtualize={true}
          columns={pipelineCols}
          rowKey={'rowKey'}
          data-test-id='imported-info-pipelines-table'
          dataSource={pipelines.map((component, i) => ({ ...component, rowKey: i }))}
        />
      </StackItem>
    </Stack>
  );
};
